<template>
  <v-container
    v-if="control"
    class="control-container"
  >
    <template v-if="isVisible && isControlType(TEXTBOX)">
      <InputText
        v-model="control.value"
        :class="control.containerClass"
        :label="getControlLabel(controlText)"
        :placeholder="controlText.placeholderText"
        :hint="controlText.comment"
        :rules="getFieldRules(control)"
        :disabled="false"
        persistent-hint
      />
    </template>

    <template v-if="isVisible && isControlType(DATE)">
      <DatePicker
        :key="control.value?.value"
        v-model="dateProxy"
        :class="control.containerClass"
        :label="getControlLabel(controlText)"
        :placeholder="controlText.placeholderText"
        :hint="controlText.comment"
        :rules="getFieldRules(control)"
        :disabled="false"
        persistent-hint
      />
    </template>

    <template v-if="isVisible && isControlType(TEXTAREA)">
      <InputText
        v-model="control.value"
        :class="control.containerClass"
        :label="getControlLabel(controlText)"
        :placeholder="controlText.placeholderText"
        :hint="controlText.comment"
        :rules="getFieldRules(control)"
        :multiline="true"
        :disabled="false"
        persistent-hint
      />
    </template>

    <template v-if="isVisible && isControlType(RADIO)">
      <v-radio-group
        v-model="control.value"
        :class="control.containerClass"
        :label="getControlLabel(controlText)"
        :hint="controlText.comment"
        :rules="getFieldRules(control)"
        :inline="!!control.inLine"
        :disabled="false"
        persistent-hint
        class="control-radio-group"
      >
        <v-radio
          v-for="(item, index) in control.items"
          :key="item"
          :label="controlText.items[index]"
          :value="item"
          :disabled="false"
        />
      </v-radio-group>
    </template>

    <template v-if="isVisible && isControlType(CHECKBOX)">
      <v-checkbox
        :key="`checkbox-${control.uniqueId}`"
        :model-value="checkboxValue"
        :class="control.containerClass"
        :label="getControlLabel(controlText)"
        :hint="controlText.comment"
        :rules="getFieldRules(control)"
        :disabled="false"
        class="control-checkbox"
        persistent-hint
        @update:model-value="checkboxValue = $event"
      />
    </template>

    <template v-if="isVisible && isControlType(DROPDOWN)">
      <div class="control-dropdown">
        <Dropdown
          v-model="control.value"
          :class="control.containerClass"
          :label="getControlLabel(controlText)"
          :items="getDropdownItems"
          item-title="title"
          item-value="value"
          :hint="controlText.comment"
          :rules="getFieldRules(control)"
          :disabled="false"
          clearable
          persistent-hint
        />
      </div>
    </template>

    <template v-if="isVisible && isControlType(GRID) && control.gridValues">
      <v-table density="compact">
        <thead>
          <tr>
            <th
              v-for="column in gridColumns"
              :id="column.value"
              :key="column.value + '-column'"
            >
              {{ column.text }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, vindex) in control.gridValues"
            :key="`${vindex}-gridValues`"
          >
            <td
              v-for="(column, cindex) in gridColumns"
              :key="column.value + '-row'"
            >
              <InputText v-model="control.gridValues[vindex][cindex]" />
            </td>
          </tr>
        </tbody>
      </v-table>
      <Button
        class="add-new-line-button"
        @click="gridAddNewLine(control)"
      >
        Add New Line
      </Button>
    </template>

    <template v-if="isVisible && isControlType(IMAGE)">
      <v-img
        :src="control.source"
        :class="['control-image', control.containerClass]"
      />
    </template>

    <template v-if="isVisible && isControlType(LABEL)">
      <div
        :class="['control-label', control.containerClass]"
      >
        <div v-html="controlText.label" />
      </div>
    </template>

    <template v-if="isVisible && isControlType(LINK)">
      <div
        :class="['control-link', control.containerClass]"
      >
        <a
          :key="`${control.uniqueId}-link`"
          :href="control.source"
          target="_blank"
        >
          {{ controlText.label }}
        </a>
      </div>
    </template>

    <template
      v-if="isVisible && isControlType(BUTTON)"
      :class="control.containerClass"
    >
      <div
        :class="['control-button', control.containerClass]"
      >
        {{ controlText.comment }}
        <Button
          class="text-white"
          @click="onButtonClick(control)"
        >
          {{ controlText.label }}
        </Button>
        <v-data-table
          v-if="control.label === 'Upload medical reports'"
          :headers="headers"
          :items="supportingFiles"
          :no-data-text="t('No data available')"
          hide-default-footer
          density="compact"
          class="mt-4"
        >
          <template
            #item.updatedAt="{ item }"
            class="text-no-wrap"
          >
            {{ normalizeDate(item.updatedAt, true) ?? '-' }}
          </template>
        </v-data-table>
      </div>
      <!-- file upload -->
      <SampleUploadDialog
        v-model="showSampleUploadDialog"
        :title="t('Upload - Order {orderId}', { orderId: orderId.toString() })"
        :initial-supporting-files="supportingFiles"
        :save-in-progress="saveInProgress"
        :error-on-save="errorOnSave"
        max-width="700"
        cancel-text="Cancel"
        @save="sampleUploadSave"
        @cancel="closeSampleUploadDialog"
      />
    </template>
  </v-container>
</template>
<script setup lang="ts">
import {ref, computed, watch} from 'vue'
import {useOrdersStore} from '@/stores/orders'
import InputText from '@/components/input-components/InputText.vue'
import Dropdown from '@/components/input-components/Dropdown.vue'
import Button from '@/components/input-components/Button.vue'
import DatePicker from '@/components/input-components/DatePicker.vue'
import SampleUploadDialog from '@/components/dialogs/SampleUploadDialog.vue'
import {useI18n} from 'vue-i18n'
import {promisedTimeout, stringEndsWith} from '@/utils'
import NotificationMessageService from '@/services/notificationmessage.service'
import type {FormControl, GridColumn} from '@/entities/form-control'
import {CONSENT_FORM, EMAIL_REG_EXP} from "@/utils/constants";
import {
  BUTTON,
  CHECKBOX,
  DATE,
  DROPDOWN,
  GRID,
  IMAGE,
  LABEL,
  LINK,
  RADIO,
  TEXTAREA,
  TEXTBOX,
} from "@/entities/form-control-types";
import { useControlRenderer } from '@/composables/useControlRenderer'
import { useDateHelper, normalizeDate } from "@/composables/useDateHelper"
import {ReportDeletePayload, ReportManagementPayload, ReportUI} from "@/entities/report";
import { createUploadError, UploadError, UploadErrorCode } from "@/entities/file";

const { t, locale } = useI18n();
const { toISO } = useDateHelper()
const { showControl } = useControlRenderer()

const props = defineProps<{ controlId: string; tabId?: string }>()
const ordersStore = useOrdersStore()

const headers = computed(() => [
  { title: t("File name"), value: "fileName", sortable: false },
  { title: t("Comment"), value: "comment", sortable: false },
  { title: t("Updated at"), value: "updatedAt", sortable: false, width: "170px", headerProps: { class: "text-no-wrap" } },
  { title: "", value: "actions", sortable: false, width: "60px" },
])

const isVisible = computed(() => {
  if (!control.value) return false
  return showControl(control.value)
})

const reports = ref<ReportUI[]>([])
const control = computed<FormControl | undefined>(() =>
  ordersStore.getOrderFormControlById(props.controlId)
)
const controlText = ref({} as FormControl)
const showSampleUploadDialog = ref(false)
const saveInProgress = ref(false)
const errorOnSave = ref(false)

const type = computed(() => String(control.value?.type).toUpperCase())
const supportingFiles = computed(() => ordersStore.getOrderSupportingFiles)
const orderId = computed(() => ordersStore.getOrder.orderId)

const getDropdownItems = computed(() => {
  if (!control.value?.items) return [];
  return control.value.items.map((val, idx) => ({
    title: controlText.value.items?.[idx] ?? val,
    value: val,
  }));
});

const gridColumns = computed<GridColumn[]>(() => {
  const items = control.value?.items;
  if (Array.isArray(items) && items.length > 0 && typeof items[0] === 'object' && 'value' in items[0]) {
    return items as GridColumn[];
  }
  return [];
});

const dateProxy = computed<string | null>({
  get: () => {
    const v = control.value?.value;
    return normalizeDate(v);
  },
  set: (val) => {
    if (!control.value) return;
    control.value.value = val;
  }
});

const checkboxValue = computed<boolean>({
  get: () => Boolean(control.value?.value),
  set: (val: boolean) => {
    if (control.value) {
      control.value.value = val
    }
  }
})
watch([control, locale] as const,
  () => {
  const c = control.value
  if (!c) {
    controlText.value = {} as FormControl;
    return;
  }

  controlText.value = buildControlText(c)
}, { immediate: true });


function buildControlText(c: FormControl): FormControl {
  const text: FormControl = {
    ...c,
    items: Array.isArray(c.items) ? [...c.items] : c.items,
  }

  if (locale.value !== 'en' && c.localizations) {
    const loc = c.localizations[locale.value] ?? {}

    if (loc.label) {
      text.label = loc.label
    }

    if (Array.isArray(loc.items)) {
      text.items = c.items?.map((item, index) => {
        return loc.items?.[index] ?? item
      }) ?? loc.items
    }
  }

  return text
}

function gridAddNewLine(c: FormControl) {
  const newLine = c.items.map(() => "")
  c.gridValues = c.gridValues ?? []
  c.gridValues.push(newLine)
}

function getControlLabel(c: FormControl) {
  let label = c.label
  if (c.validations?.some(v => v.ruleType === 'required')) label += '*'
  return label
}

function getFieldRules(c: FormControl) {
  const rules = []
  if (!showControl(c)) return rules

  if (c.validations?.some(v => v.ruleType === 'required')) {
    rules.push(v => !!v || t('This field is required'))
  }
  if (c.validations?.some(v => v.ruleType === 'numbersOnly')) {
    rules.push(v => /^\d*$/.test(v) || t('This field must contain only numbers'))
  }
  if (c.validations?.some(v => v.ruleType === 'textOnly')) {
    rules.push(v => /^[a-zA-Z ]*$/.test(v) || t('This field must contain only letters'))
  }
  if (c.validations?.some(v => v.ruleType === 'isEmail')) {
    rules.push(v =>
      !v || EMAIL_REG_EXP.test(v) || t('This field must contain a valid e-mail')
    )
  }

  return rules
}

async function onButtonClick(c: FormControl) {
  const name = c.name ?? '';

  // Handle upload button
  if (c.buttonType === 'upload' || c.label?.toLowerCase().includes('upload')) {
    showSampleUploadDialog.value = true;
    return;
  }

  // Handle consent download button
  if (stringEndsWith(name, CONSENT_FORM.downloadBtnNameSuffix)) {
    const type = name.replace(CONSENT_FORM.downloadBtnNameSuffix, "") ?? null;
    if (!type) {
      NotificationMessageService.showError("Consent form type not found");
      return;
    }

    const tab = ordersStore.getOrderFormTabByControlId(c.uniqueId)
    if (!tab) {
      NotificationMessageService.showError("Consent form tab not found");
      return;
    }

    const template = tab.template || CONSENT_FORM.defaultTemplate;

    const languageControls = ordersStore.getOrderFormControlsByName?.(
      `${type}${CONSENT_FORM.languageNameSuffix}`
    ) ?? []
    const languageControl = languageControls.find(c => tab.controls.includes(c.uniqueId))
    const rawLanguage = languageControl?.value
    const language =
      typeof rawLanguage === 'string'
        ? rawLanguage
        : (rawLanguage as any)?.value ?? CONSENT_FORM.defaultLanguage

    const payload = {
      orderId: orderId.value,
      type,
      data: {
        language,
        formData: ordersStore.getOrderForm,
      },
      template,
    };
    await ordersStore.fetchOrderConsent(payload)
  }
}

function closeSampleUploadDialog() {
  showSampleUploadDialog.value = false
}

async function sampleUploadSave(payload: ReportManagementPayload) {
  saveInProgress.value = true;
  errorOnSave.value = false;

  const changesSummary = { add: 0, remove: 0, update: 0 };
  const errors: UploadError[] = [];

  try {
    if (payload.remove?.length) {
      for (const file of payload.remove) {
        const ok = await ordersStore.deleteOrderSupportingFile({ orderFileId: file.orderFileId }, false);
        if (ok) {
          changesSummary.remove++;
        } else {
          errors.push(createUploadError(file.fileName || `ID ${file.orderFileId}`, UploadErrorCode.DELETE_FAILED));
        }
        if (changesSummary.remove % 5 === 0) await promisedTimeout(300);
      }
    }

    if (payload.add?.length) {
      const result = await ordersStore.syncOrderSupportingFiles(orderId.value, payload.add);
      changesSummary.add = result.addedCount;
      if (!result.success && result.errors.length) {
        errors.push(...result.errors);
      }
    }

    if (payload.update?.length) {
      for (const item of payload.update) {
        const ok = await ordersStore.putOrderSupportingFile({
          orderFileId: item.orderFileId,
          comment: item.comment,
          file: item.file || null,
        }, false);
        if (ok) {
          changesSummary.update++;
        } else {
          errors.push(createUploadError(`ID ${item.orderFileId}`, UploadErrorCode.UPDATE_FAILED));
        }
      }
    }

    const putResult = await ordersStore.putOrder([false]);
    if (putResult === true) {
      errors.push(createUploadError('order', UploadErrorCode.FINALIZATION_FAILED, 'Put order failed'));
    }

    const hasSuccess = changesSummary.add > 0 || changesSummary.remove > 0 || changesSummary.update > 0;
    const hasErrors = errors.length > 0;

    if (hasSuccess && !hasErrors) {
      NotificationMessageService.showSuccess(buildSuccessMessage(changesSummary));
      if (payload.remove?.length && !payload.add?.length && !payload.update?.length) {
        closeSampleUploadDialog();
      }
    } else if (hasSuccess && hasErrors) {
      const successMsg = buildSuccessMessage(changesSummary);
      const errorList = formatErrors(errors);
      NotificationMessageService.showWarning(
        `${t('upload.partial_success')} ${successMsg}\n${t('upload.failed_files')}:\n${errorList}`
      );
    } else {
      errorOnSave.value = true;
      const errorMsg = errors.length ? `${t('upload.failure')}\n${formatErrors(errors)}` : t('upload.failure');
      NotificationMessageService.showError(errorMsg);
    }

    await ordersStore.fetchOrderSupportingFiles(orderId.value, false);

  } catch (e) {
    console.error(e);
    errorOnSave.value = true;
    NotificationMessageService.showError(t('error.unexpected'));
  } finally {
    saveInProgress.value = false;
  }
}

function isControlType(expectedType: string) {
  return String(control.value?.type).toLowerCase() === expectedType
}

function buildSuccessMessage(summary: { add: number; remove: number; update: number }): string {
  const parts = [];
  if (summary.remove) parts.push(t('files_deleted', { count: summary.remove }));
  if (summary.add) parts.push(t('files_added', { count: summary.add }));
  if (summary.update) parts.push(t('files_updated', { count: summary.update }));
  return parts.length ? parts.join(', ') : t('no_files_changes');
}

function formatErrors(errors: UploadError[]): string {
  return errors.map(err => {
    const code = err.code || UploadErrorCode.UNKNOWN;
    const translated = t(`errors.${code}`, { fileName: err.fileName }) || `${err.fileName} – ${code}`;
    return `• ${translated}`;
  }).join('\n');
}

</script>

<style lang="scss">
.control-container {
  padding: 0;

  .v-label {
    opacity: var(--v-radio-label-opacity) !important;
  }

  .v-selection-control--dirty {
    .v-icon {
      color: rgb(var(--v-theme-primary)) !important;
    }
  }

  .control-checkbox {
    margin-top: 0;
    padding-left: 3px;

    .v-input__details {
      display: none;
    }

    .v-messages {
      min-height: 0.125rem;
    }
  }

  .control-dropdown {
    margin-top: 0;
  }

  .control-link {
    margin: 6px;
  }

  .control-image {
    margin-top: 3px;
    margin-bottom: 18px;
  }

  .control-label {
    text-align: justify;
    margin-bottom: 18px;
    white-space: pre-wrap;
  }

  .control-button {
    text-align: left;
    margin-bottom: 18px;
  }

  .add-new-line-button {
    margin-bottom: 15px;
  }

  .control-radio-group {
    &.v-input--radio-group--column .v-radio:not(:last-child):not(:only-child) {
      margin-bottom: 0.625rem;
    }

    .v-label:first-child {
      margin-inline-start: 8px;
      padding-bottom: 8px;
    }

    .v-selection-control-group {
      margin-top: 0 !important;
    }

    margin-top: 16px;
  }

  .v-input--error {
    color: rgb(var(--v-theme-error));
  }

  .v-input__details {
    min-height: unset;
    padding-top: unset;
  }

  .v-field {
    margin-bottom: 8px;
  }

  .v-messages {
    min-height: 0.125rem;
    display: flex;
    padding-top: 0;
    margin-bottom: 8px;
    text-align: left;
  }
}
</style>
